/**
 * AddTagDropdown Component
 *
 * A dropdown for adding tags to an investment.
 * Shows existing tags with search/filter, and allows creating new tags.
 */

import { useTags, useAddTag } from '@medsuite/api-client';
import React, { useState, useRef, useEffect } from 'react';

import type { TagWithCount, InvestmentTag } from '@medsuite/api-client';

export interface AddTagDropdownProps {
  /** The investment ID to add tags to */
  investmentId: number;
  /** Tags already assigned to this investment (to exclude from suggestions) */
  existingTags: InvestmentTag[];
  /** Called after a tag is successfully added */
  onTagAdded?: () => void;
}

export function AddTagDropdown({ investmentId, existingTags, onTagAdded }: AddTagDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch all tags
  const { data: allTags = [], isLoading: isLoadingTags } = useTags();

  // Add tag mutation
  const addTagMutation = useAddTag();

  // Filter out already-assigned tags and apply search
  const existingTagIds = new Set(existingTags.map((t) => t.tag_id));
  const availableTags = allTags
    .filter((tag) => !existingTagIds.has(tag.tag_id))
    .filter((tag) =>
      searchText ? tag.tag_name.toLowerCase().includes(searchText.toLowerCase()) : true
    )
    .sort((a, b) => a.tag_name.localeCompare(b.tag_name));

  // Check if the search text matches an existing tag (case-insensitive)
  const exactMatch = allTags.find((tag) => tag.tag_name.toLowerCase() === searchText.toLowerCase());
  const canCreateNew = searchText.trim() && !exactMatch;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchText('');
    }
  };

  const handleAddTag = async (tag: TagWithCount) => {
    try {
      await addTagMutation.mutateAsync({
        investmentId,
        data: { tag_id: tag.tag_id },
      });
      setSearchText('');
      setIsOpen(false);
      onTagAdded?.();
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  };

  const handleCreateTag = async () => {
    if (!searchText.trim()) {
      return;
    }

    try {
      await addTagMutation.mutateAsync({
        investmentId,
        data: { tag_name: searchText.trim() },
      });
      setSearchText('');
      setIsOpen(false);
      onTagAdded?.();
    } catch (error) {
      console.error('Failed to create tag:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchText('');
    } else if (e.key === 'Enter' && canCreateNew) {
      handleCreateTag();
    }
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Add Tag Link */}
      <button
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400
          hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
      >
        <svg
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        Add Tag
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="absolute left-0 z-50 mt-1 w-56 rounded-md bg-white dark:bg-gray-800
            shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="p-2">
            <input
              ref={inputRef}
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or create tag..."
              className="w-full rounded-md border border-gray-300 dark:border-gray-600
                bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100
                placeholder-gray-400 dark:placeholder-gray-500
                focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1
                focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>

          {/* Tags List */}
          <div className="max-h-48 overflow-y-auto">
            {isLoadingTags ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                Loading tags...
              </div>
            ) : availableTags.length === 0 && !canCreateNew ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                {searchText ? 'No matching tags' : 'No available tags'}
              </div>
            ) : (
              <ul className="py-1">
                {availableTags.map((tag) => (
                  <li key={tag.tag_id}>
                    <button
                      type="button"
                      onClick={() => handleAddTag(tag)}
                      disabled={addTagMutation.isPending}
                      className="flex w-full items-center justify-between px-4 py-2 text-sm
                        text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>{tag.tag_name}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {tag.investment_count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Create New Tag Option */}
          {canCreateNew && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-2">
              <button
                type="button"
                onClick={handleCreateTag}
                disabled={addTagMutation.isPending}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium
                  text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30
                  hover:bg-green-100 dark:hover:bg-green-900/50
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Create &quot;{searchText.trim()}&quot;
              </button>
            </div>
          )}

          {/* Loading State */}
          {addTagMutation.isPending && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-800/50 rounded-md">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AddTagDropdown;
